/**
 * App shell: sidebar (drawer on mobile) + topbar + routed content.
 * A pathless layout route in src/app.tsx mounts it for project pages (/p/:projectSlug/*) and the
 * workspace pages built on it (documents, projects, settings); child routes render into the <Outlet/>.
 */

import { useCallback, useEffect, useState } from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { useIsMobile } from '../../hooks/use-is-mobile'
import { useProjects } from '../../hooks/use-projects'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { CommandPalette } from '../command-palette/command-palette'
import { ShellSidebar } from './shell-sidebar'
import { ShellTopbar } from './shell-topbar'

export function AppShell() {
  const t = useT()
  const isMobile = useIsMobile()
  const { pathname } = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])
  const { projectSlug } = useParams<{ projectSlug: string }>()
  const { data: projects } = useProjects()
  const setLastProjectSlug = useAppStore((s) => s.setLastProjectSlug)

  // Remember the project being viewed (only real ones) so /p and workspace pages resume it
  useEffect(() => {
    if (projectSlug && projects?.some((p) => p.slug === projectSlug)) setLastProjectSlug(projectSlug)
  }, [projectSlug, projects, setLastProjectSlug])

  // Close the drawer on navigation and when leaving the mobile breakpoint
  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname, isMobile])

  // Lock page scroll and allow Escape while the drawer is open
  useEffect(() => {
    if (!drawerOpen) return
    document.body.style.overflow = 'hidden'
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [drawerOpen])

  return (
    <div className="flex h-dvh overflow-hidden bg-surface font-sans text-fg antialiased">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-border md:flex">
        <ShellSidebar />
      </aside>

      {/* Mobile drawer (always mounted; slides via transform) */}
      {isMobile && (
        <>
          <div
            className={cn('drawer-backdrop fixed inset-0 z-50 bg-black/50 backdrop-blur-sm', drawerOpen && 'drawer-open')}
            onClick={closeDrawer}
            aria-hidden="true"
          />
          <aside
            className={cn('drawer-left fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] border-r border-border', drawerOpen && 'drawer-open')}
            aria-label={t('shell.navigation')}
            aria-hidden={!drawerOpen}
            inert={!drawerOpen}
          >
            <ShellSidebar onClose={closeDrawer} />
          </aside>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <ShellTopbar onOpenMenu={() => setDrawerOpen(true)} />
        <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      <CommandPalette />
    </div>
  )
}
